/**
 * Artwork Form Web Component
 *
 * This module provides a custom web component for handling artwork search and selection
 * functionality in product forms. It integrates with an external artwork management system
 * to search for approved artwork based on order numbers or email addresses.
 *
 * Key Features:
 * - Debounced artwork search with real-time feedback
 * - Multiple artwork option support (front, back, sleeve, etc.)
 * - Blank selection logic with intelligent restrictions
 * - State management with event broadcasting
 * - Artwork gallery integration with mockup updates
 * - Universal slide mapping for any product type
 * - Error handling and loading states
 * - Translation support for all user-facing strings
 *
 * Usage:
 * <artwork-form data-product-handle="product-handle" data-customer-email="email@example.com">
 *   <!-- Artwork form content -->
 * </artwork-form>
 *
 * Events Dispatched:
 * - artwork:statechange - Fired when artwork state changes
 * - artwork:selected - Fired when artwork is selected and gallery updated
 *
 * Dependencies:
 * - External artwork API (oms.thecustomcaptain.com)
 * - Product media gallery with Swiper integration
 * - Translation strings from window.product_form_t
 *
 * @version 2.0.0
 * @author The Custom Captain Development Team
 */

// Constants for configuration
const ARTWORK_CONFIG = {
  API: {
    BASE_URL: 'https://oms.thecustomcaptain.com',
    SEARCH_ENDPOINT: '/search',
    DEBOUNCE_DELAY: 500
  },
  SELECTORS: {
    INPUT: 'input[type="text"][name^="properties[Order"]',
    CARD: '[id*="artwork-details-card"]',
    LOADING: '.artwork-details-card-loading',
    OPTIONS: '.artwork-details-card-options',
    NOT_AVAILABLE: '[data-artwork-not-available]',
    NOT_APPROVED: '[data-artwork-not-approved]',
    ARTWORK_OPTION: '.artwork-option',
    ARTWORK_SWATCHES: '.artwork-swatches'
  },
  ARTWORK_STATUS: {
    COMPLETE: 'complete',
    CUSTOMER_APPROVED: 'customer_approved',
    CANCELLED: 'cancelled'
  },
  BLANK_ARTWORK_ID: 'blank',
  TRANSITIONS: {
    DURATION: 300,
    DELAY: 50
  },
  LOCAL_STORAGE: {
    LAST_SEARCH_KEY: 'artwork_form_last_search',
    ORDER_ENTERED_KEY: 'artwork_order_entered'
  }
};


/**
 * ArtworkForm Custom Element
 *
 * Manages artwork search, selection, and state for product forms.
 * Handles integration with external artwork API and product gallery updates.
 */
class ArtworkForm extends HTMLElement {
  constructor() {
    super();
    this.initializeVariables();
    this.bindEventHandlers();
  }

  /**
   * Initialize component variables and state
   * @private
   */
  initializeVariables() {
    this.sectionId = this.getSectionId();
    this.productHandle = this.dataset.productHandle || '';
    this.customerEmail = this.dataset.customerEmail || '';
    this.disableLeaveBlankInArtwork = this.dataset.disableLeaveBlankInArtwork === 'true';

    this.initializeDOMElements();
    this.initializeState();
    this.initializeTranslations();
  }

  /**
   * Get section ID from closest element
   * @private
   * @returns {string} Section ID
   */
  getSectionId() {
    return this.closest('[data-section-id]')?.dataset.sectionId || '';
  }

  /**
   * Initialize DOM element references
   * @private
   */
  initializeDOMElements() {
    this.input = this.querySelector(ARTWORK_CONFIG.SELECTORS.INPUT);
    this.card = this.querySelector(`#artwork-details-card-${this.sectionId}`);
    this.loadingEl = this.querySelector(ARTWORK_CONFIG.SELECTORS.LOADING);
    this.optionsEl = this.querySelector(ARTWORK_CONFIG.SELECTORS.OPTIONS);
    this.notAvailableEl = this.querySelector(ARTWORK_CONFIG.SELECTORS.NOT_AVAILABLE);
    this.notApprovedEl = this.querySelector(ARTWORK_CONFIG.SELECTORS.NOT_APPROVED);

    this.confirmationEl = this.querySelector('[data-artwork-confirmation]');
    this.noBannerEl = this.querySelector('[data-artwork-no-banner]');
    this.goBackBtn = this.querySelector('[data-artwork-go-back]');
    this.inputWrapperEl = this.querySelector('[data-artwork-input-wrapper]');
    this.skipConfirmation = this.dataset.skipConfirmation === 'true';
    this.artworkPurchaseUrl = this.dataset.artworkPurchaseUrl || '/products/digital-file-only';
  }

  /**
   * Initialize component state
   * @private
   */
  initializeState() {
    this.debounceTimer = null;
    this.isSearching = false;
    this.artworkRequirements = new Map();
    this.currentArtworks = [];
  }

  /**
   * Initialize translation strings
   * @private
   */
  initializeTranslations() {
    this.translations = window.product_form_t || {
      artworkLoading: 'Loading artwork...',
      noArtworkAvailable: 'No artwork available',
      artworkNotApproved: 'Artwork is not approved',
      selectArtwork: 'Select artwork',
      enterOrderOrEmail: 'Order or Email',
      searchingForArtwork: 'Searching for artwork...',
      blankSelectionRestriction: 'Cannot select blank {option_type} when {selected_type} is blank. Only one blank option allowed.'
    };
  }

  /**
   * Bind event handler methods to preserve context
   * @private
   */
  bindEventHandlers() {
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleArtworkSelection = this.handleArtworkSelection.bind(this);
    this.dispatchArtworkStateChange = this.dispatchArtworkStateChange.bind(this);
  }

  /**
   * Initialize component when connected to DOM
   */
  connectedCallback() {
    setTimeout(() => {
      this.initializeVariables();
      this.attachEventListeners();
      this.setupFormValidation();
      this.initializeConfirmation();
    }, 10);
  }

  /**
   * Clean up when component is disconnected from DOM
   */
  disconnectedCallback() {
    this.detachEventListeners();
    this.clearDebounceTimer();
  }

  /**
   * Reinitialize component (useful after DOM updates)
   */
  reinitialize() {
    this.disconnectedCallback();
    this.connectedCallback();
  }

  /**
   * Attach event listeners to DOM elements
   * @private
   */
  attachEventListeners() {
    if (this.input) {
      this.input.addEventListener('input', this.handleInputChange);
    }

    if (this.optionsEl) {
      this.optionsEl.addEventListener('change', this.handleArtworkSelection);
    }
  }

  /**
   * Detach event listeners from DOM elements
   * @private
   */
  detachEventListeners() {
    if (this.input) {
      this.input.removeEventListener('input', this.handleInputChange);
    }

    if (this.optionsEl) {
      this.optionsEl.removeEventListener('change', this.handleArtworkSelection);
    }
  }

  /**
   * Initialize confirmation flow or skip directly to input
   * @private
   */
  initializeConfirmation() {
    const hasStoredOrder = localStorage.getItem(ARTWORK_CONFIG.LOCAL_STORAGE.ORDER_ENTERED_KEY) === 'true';
    const hasPreviousSearch = !!localStorage.getItem(ARTWORK_CONFIG.LOCAL_STORAGE.LAST_SEARCH_KEY);

    if (this.skipConfirmation || hasStoredOrder || hasPreviousSearch) {
      this.showInputDirectly(hasStoredOrder || hasPreviousSearch);
      return;
    }

    if (this.confirmationEl) {
      this.confirmationEl.querySelectorAll('[data-artwork-confirm]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const choice = e.currentTarget.dataset.artworkConfirm;

          if (choice === 'yes') {
            this.handleConfirmYes();
          } else {
            this.handleConfirmNo();
          }
        });
      });
    }

    if (this.goBackBtn) {
      this.goBackBtn.addEventListener('click', () => {
        this.handleGoBack();
      });
    }

    if (this.card) {
      const observer = new MutationObserver(() => {
        if (this.card.style.display !== 'none') {
          this.showInputDirectly(true);
          if (this.input && this.input.value.trim()) {
            localStorage.setItem(ARTWORK_CONFIG.LOCAL_STORAGE.LAST_SEARCH_KEY, this.input.value.trim());
            localStorage.setItem(ARTWORK_CONFIG.LOCAL_STORAGE.ORDER_ENTERED_KEY, 'true');
          }
          observer.disconnect();
        }
      });
      observer.observe(this.card, { attributes: true, attributeFilter: ['style'] });
    }
  }

  /**
   * Show input directly, skipping confirmation
   * @private
   */
  showInputDirectly(fromStorage) {
    if (this.confirmationEl) {
      this.confirmationEl.style.display = 'none';
    }

    if (this.inputWrapperEl) {
      this.inputWrapperEl.style.display = '';
    }

    if (fromStorage && !this.customerEmail) {
      this.handleLocalStoragePrefill();
    } else {
      this.handleLocalStoragePrefill();
      this.handleCustomerEmailPrefill();
    }

    this.initializeExistingSelections();
  }

  /**
   * Handle "Yes" confirmation click
   * @private
   */
  handleConfirmYes() {
    if (this.confirmationEl) {
      this.confirmationEl.style.display = 'none';
    }

    if (this.noBannerEl) {
      this.noBannerEl.style.display = 'none';
    }

    if (this.inputWrapperEl) {
      this.inputWrapperEl.style.display = '';
    }

    if (this.input) {
      this.input.focus();
    }

    this.handleLocalStoragePrefill();
    this.initializeExistingSelections();
  }

  /**
   * Handle "No" confirmation click
   * @private
   */
  handleConfirmNo() {
    if (this.confirmationEl) {
      this.confirmationEl.style.display = 'none';
    }

    if (this.noBannerEl) {
      this.noBannerEl.style.display = '';
    }

    if (this.input) {
      this.input.removeAttribute('required');
    }
  }

  /**
   * Handle "Go back" click — return to confirmation question
   * @private
   */
  handleGoBack() {
    if (this.noBannerEl) {
      this.noBannerEl.style.display = 'none';
    }

    if (this.confirmationEl) {
      this.confirmationEl.style.display = '';
    }

    if (this.input) {
      this.input.setAttribute('required', '');
    }
  }

  /**
   * Handle customer email prefill if available
   * @private
   */
  handleCustomerEmailPrefill() {
    if (this.customerEmail && this.input) {
      if (!this.input.value) {
        this.input.value = this.customerEmail;
      }

      if (this.input.value === this.customerEmail) {
        window.dispatchEvent(new CustomEvent('artwork:prefill', { detail: this.customerEmail }));
        setTimeout(() => this.searchArtwork(this.customerEmail), 100);
      }
    }
  }

  /**
   * Clear debounce timer if active
   * @private
   */
  clearDebounceTimer() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  /**
   * Handle input change events with debouncing
   * @private
   * @param {Event} event - Input change event
   */
  handleInputChange(event) {
    const term = event.target.value.trim();

    this.clearDebounceTimer();
    this.dispatchArtworkStateChange();

    if (term.length === 0) {
      this.clearArtwork();
      return;
    }

    localStorage.setItem(ARTWORK_CONFIG.LOCAL_STORAGE.LAST_SEARCH_KEY, term);
    localStorage.setItem(ARTWORK_CONFIG.LOCAL_STORAGE.ORDER_ENTERED_KEY, 'true');

    this.debounceTimer = setTimeout(() => {
      this.searchArtwork(term);
    }, ARTWORK_CONFIG.API.DEBOUNCE_DELAY);
  }

  /**
   * Handle artwork selection changes
   * @private
   * @param {Event} event - Change event from artwork options
   */
  handleArtworkSelection(event) {
    if (!event.target.matches('input[type="radio"]')) return;

    const artworkType = this.extractArtworkType(event.target);
    if (!artworkType) return;

    // Ensure input is actually checked before proceeding
    if (!event.target.checked) {
      this.updateArtworkRequirement(artworkType, false);
      this.dispatchArtworkStateChange();
      return;
    }

    // Validate that the input has a value
    if (!event.target.value || event.target.value.trim() === '') {
      console.warn(`Artwork selection missing value for ${artworkType}`);
      return;
    }

    this.updateArtworkRequirement(artworkType, event.target.checked);
    this.syncWithProductFormState(artworkType, true);
    this.handleBlankSelectionLogic(event.target, artworkType);
    this.updateGalleryWithArtwork(event.target);
    
    // Immediately ensure the property is in the form (critical for app integrations)
    const form = this.findActualFormElement();
    if (form) {
      this.ensureArtworkPropertyInForm(form, artworkType, event.target);
    }
    
    this.dispatchArtworkStateChange();
    
    // Double-check the property is set after a brief delay (for race conditions)
    setTimeout(() => {
      this.verifyArtworkPropertySet(artworkType, event.target);
      // Ensure it's in the form again after delay
      if (form) {
        this.ensureArtworkPropertyInForm(form, artworkType, event.target);
      }
    }, 100);
  }

  /**
   * Extract artwork type from input element
   * @private
   * @param {HTMLInputElement} input - Input element
   * @returns {string|null} Artwork type or null
   */
  extractArtworkType(input) {
    if (!input || !input.name) return null;
    const match = input.name.match(/properties\[(.*?)\]/);
    return match ? match[1] : null;
  }

  /**
   * Update artwork requirement state
   * @private
   * @param {string} artworkType - Type of artwork
   * @param {boolean} selected - Whether artwork is selected
   */
  updateArtworkRequirement(artworkType, selected) {
    this.artworkRequirements.set(artworkType, selected);
    
    // Clear requirement if deselected
    if (!selected) {
      this.syncWithProductFormState(artworkType, false);
    }
  }

  /**
   * Sync artwork selection state with global productFormState
   * @private
   * @param {string} artworkType - Type of artwork
   * @param {boolean} selected - Whether artwork is selected
   */
  syncWithProductFormState(artworkType, selected) {
    if (window.productFormState && window.productFormState.requiredSelections) {
      if (!window.productFormState.requiredSelections.artwork) {
        window.productFormState.requiredSelections.artwork = {};
      }
      window.productFormState.requiredSelections.artwork[artworkType] = selected;
      
      // Trigger button state check if form is available
      const form = this.closest('form[action="/cart/add"]') || 
                   this.closest('form') ||
                   document.querySelector('form[action="/cart/add"]');
      
      if (form && window.productFormState.checkButtonState) {
        window.productFormState.checkButtonState(form);
      }
    }
  }

  /**
   * Verify that artwork property is properly set on the input
   * @private
   * @param {string} artworkType - Type of artwork
   * @param {HTMLInputElement} input - Input element
   */
  verifyArtworkPropertySet(artworkType, input) {
    if (!input || !input.checked) {
      console.warn(`Artwork input for ${artworkType} is not checked`);
      return false;
    }

    if (!input.value || input.value.trim() === '') {
      console.error(`Artwork input for ${artworkType} has no value`);
      return false;
    }

    // Verify the input is in the form data
    const form = input.closest('form[action="/cart/add"]') || 
                 input.closest('form') ||
                 document.querySelector('form[action="/cart/add"]');
    
    if (form) {
      const formData = new FormData(form);
      const propertyValue = formData.get(`properties[${artworkType}]`);
      
      if (!propertyValue) {
        console.error(`Artwork property ${artworkType} not found in form data`);
        // Try to fix it by ensuring the input is properly named
        if (input.name !== `properties[${artworkType}]`) {
          console.warn(`Input name mismatch: expected properties[${artworkType}], got ${input.name}`);
        }
        return false;
      }
      
      return true;
    }
    
    return false;
  }

  /**
   * Handle blank selection logic and restrictions
   * @private
   * @param {HTMLInputElement} selectedInput - Selected input element
   * @param {string} artworkType - Type of artwork being selected
   */
  handleBlankSelectionLogic(selectedInput, artworkType) {
    if (!this.shouldApplyBlankRestrictions()) return;

    const isBlankSelected = selectedInput.dataset.artworkId === ARTWORK_CONFIG.BLANK_ARTWORK_ID;

    if (isBlankSelected && selectedInput.checked) {
      this.handleBlankSelection(artworkType);
    } else if (selectedInput.checked) {
      this.enableOtherBlankOptions(artworkType);
    }
  }

  /**
   * Determine if blank selection restrictions should be applied
   * Only applies restrictions for products with multiple artwork options
   * Single artwork products (like hats) skip blank restrictions entirely
   * @private
   * @returns {boolean} Whether to apply blank restrictions
   */
  shouldApplyBlankRestrictions() {
    const artworkOptionTypes = this.getArtworkOptionTypes();
    return artworkOptionTypes.length > 1;
  }

  /**
   * Dynamically get all artwork option types from the current product
   * Works with any number of artwork options (front, back, artwork, sleeve, etc.)
   * @private
   * @returns {Array<string>} Array of artwork option types
   */
  getArtworkOptionTypes() {
    const artworkSwatches = this.optionsEl?.querySelectorAll(`${ARTWORK_CONFIG.SELECTORS.ARTWORK_SWATCHES}[data-option]`) || [];
    return Array.from(artworkSwatches)
      .map(swatch => swatch.dataset.option)
      .filter(Boolean);
  }

  /**
   * Universal blank selection handler - works with any number of artwork options
   * Prevents multiple blank selections across all artwork types
   * @private
   * @param {string} selectedBlankType - Type of blank being selected
   */
  handleBlankSelection(selectedBlankType) {
    const allOptionTypes = this.getArtworkOptionTypes();
    const otherOptionTypes = allOptionTypes.filter(type => type !== selectedBlankType);

    // Disable blank options for all other option types
    otherOptionTypes.forEach(optionType => {
      this.disableBlankOption(optionType, selectedBlankType);
      this.selectNonBlankAlternative(optionType);
    });
  }

  /**
   * Disable blank option for a specific artwork type
   * @private
   * @param {string} optionType - Artwork type to disable
   * @param {string} selectedBlankType - Type that is currently blank
   */
  disableBlankOption(optionType, selectedBlankType) {
    const blankInput = this.querySelector(`input[data-artwork-id="${ARTWORK_CONFIG.BLANK_ARTWORK_ID}"][name="properties[${optionType}]"]`);
    if (!blankInput) return;

    const selectedTypeName = this.getOptionDisplayName(selectedBlankType);
    const optionTypeName = this.getOptionDisplayName(optionType);
    const tooltipMessage = this.createBlankRestrictionMessage(optionTypeName, selectedTypeName);

    this.setBlankInputState(blankInput, true, tooltipMessage);
  }

  /**
   * Select non-blank alternative when blank is disabled
   * @private
   * @param {string} optionType - Artwork type to update
   */
  selectNonBlankAlternative(optionType) {
    const currentBlankInput = this.querySelector(`input[data-artwork-id="${ARTWORK_CONFIG.BLANK_ARTWORK_ID}"][name="properties[${optionType}]"]`);

    if (currentBlankInput?.checked) {
      currentBlankInput.checked = false;

      const firstNonBlankOption = this.querySelector(`input[name="properties[${optionType}]"]:not([data-artwork-id="${ARTWORK_CONFIG.BLANK_ARTWORK_ID}"])`);
      if (firstNonBlankOption) {
        firstNonBlankOption.checked = true;
        this.artworkRequirements.set(optionType, true);
        this.updateGalleryWithArtwork(firstNonBlankOption);
      }
    }
  }

  /**
   * Re-enable blank options for all other artwork types when a non-blank is selected
   * Universal method that works with any number of artwork options
   * @private
   * @param {string} selectedType - Type that was selected (non-blank)
   */
  enableOtherBlankOptions(selectedType) {
    const allOptionTypes = this.getArtworkOptionTypes();
    const otherOptionTypes = allOptionTypes.filter(type => type !== selectedType);

    // Re-enable blank options for all other option types
    otherOptionTypes.forEach(optionType => {
      const blankInput = this.querySelector(`input[data-artwork-id="${ARTWORK_CONFIG.BLANK_ARTWORK_ID}"][name="properties[${optionType}]"]`);
      if (blankInput) {
        this.setBlankInputState(blankInput, false);
      }
    });
  }

  /**
   * Set blank input state (enabled/disabled) and tooltip
   * @private
   * @param {HTMLInputElement} blankInput - Blank input element
   * @param {boolean} disabled - Whether input should be disabled
   * @param {string} tooltipMessage - Tooltip message (optional)
   */
  setBlankInputState(blankInput, disabled, tooltipMessage = '') {
    blankInput.disabled = disabled;

    if (tooltipMessage) {
      blankInput.title = tooltipMessage;
    } else {
      blankInput.removeAttribute('title');
    }

    const blankLi = blankInput.closest('li');
    if (blankLi) {
      blankLi.classList.toggle('disable', disabled);

      const label = blankLi.querySelector('label');
      if (label) {
        if (tooltipMessage) {
          label.title = tooltipMessage;
        } else {
          label.removeAttribute('title');
        }
      }
    }
  }

  /**
   * Convert technical option types to user-friendly display names
   * Examples: 'select-front' → 'front', 'select-artwork' → 'artwork'
   * @private
   * @param {string} optionType - Technical option type
   * @returns {string} User-friendly display name
   */
  getOptionDisplayName(optionType) {
    return optionType
      .replace('select-', '')
      .replace('-', ' ')
      .toLowerCase();
  }

  /**
   * Create blank restriction message with safe fallback
   * @private
   * @param {string} optionTypeName - Option type display name
   * @param {string} selectedTypeName - Selected type display name
   * @returns {string} Restriction message
   */
  createBlankRestrictionMessage(optionTypeName, selectedTypeName) {
    const template = this.translations.blankSelectionRestriction;

    if (template && typeof template === 'string') {
      return template
        .replace(/\{\{\s*option_type\s*\}\}/g, optionTypeName)
        .replace(/\{\{\s*selected_type\s*\}\}/g, selectedTypeName);
    }

    // Fallback message if translation is missing
    return `Cannot select blank ${optionTypeName} when ${selectedTypeName} is blank. Only one blank option allowed.`;
  }

  /**
   * Search for artwork using external API
   * @param {string} searchTerm - Search term (order number or email)
   */
  async searchArtwork(searchTerm) {
    try {
      this.setSearchingState(true);
      this.showLoading();
      this.dispatchArtworkStateChange();

      const artworks = await this.fetchArtworkData(searchTerm);
      const validArtworks = this.filterValidArtworks(artworks);

      if (validArtworks.length === 0) {
        this.showNotAvailable();
        this.toggleErrorMessage(false);
        return;
      }

      const approvedArtworks = this.filterApprovedArtworks(validArtworks);

      if (approvedArtworks.length === 0) {
        this.showNotApproved(validArtworks[0]);
        this.toggleErrorMessage(false);
        return;
      }

      this.currentArtworks = approvedArtworks;
      this.renderArtworkOptions(approvedArtworks);
      this.toggleErrorMessage(true);
    } catch (error) {
      console.error('Error fetching artwork:', error);
      this.showNotAvailable();
    } finally {
      this.setSearchingState(false);
      this.dispatchArtworkStateChange();
    }
  }

  toggleErrorMessage(show = false) {
    const warningMessage = document.querySelector('.product__warning');

    if (warningMessage) {
      show ? warningMessage.classList.remove('hidden') : warningMessage.classList.add('hidden');
    }
  }

  /**
   * Set searching state
   * @private
   * @param {boolean} searching - Whether search is in progress
   */
  setSearchingState(searching) {
    this.isSearching = searching;
  }

  /**
   * Fetch artwork data from external API
   * @private
   * @param {string} searchTerm - Search term
   * @returns {Promise<Array>} Artwork data array
   */
  async fetchArtworkData(searchTerm) {
    const url = `${ARTWORK_CONFIG.API.BASE_URL}${ARTWORK_CONFIG.API.SEARCH_ENDPOINT}?query=${encodeURIComponent(searchTerm)}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Filter out cancelled artworks
   * @private
   * @param {Array} artworks - Raw artwork data
   * @returns {Array} Valid artworks
   */
  filterValidArtworks(artworks) {
    return artworks.filter(artwork => artwork.artwork_status !== ARTWORK_CONFIG.ARTWORK_STATUS.CANCELLED);
  }

  /**
   * Filter for approved artworks only
   * @private
   * @param {Array} artworks - Valid artwork data
   * @returns {Array} Approved artworks
   */
  filterApprovedArtworks(artworks) {
    return artworks.filter(artwork =>
      artwork.artwork_status === ARTWORK_CONFIG.ARTWORK_STATUS.COMPLETE ||
      artwork.artwork_status === ARTWORK_CONFIG.ARTWORK_STATUS.CUSTOMER_APPROVED
    );
  }

  /**
   * Show loading state
   * @private
   */
  showLoading() {
    this.ensureCardElement();

    this.card.style.display = 'block';
    this.setElementDisplay(this.loadingEl, 'block');
    this.setElementDisplay(this.optionsEl, 'none');
    this.setElementDisplay(this.notAvailableEl, 'none');
    this.setElementDisplay(this.notApprovedEl, 'none');
  }

  /**
   * Show not available state
   * @private
   */
  showNotAvailable() {
    this.ensureCardElement();

    this.setElementDisplay(this.loadingEl, 'none');
    this.setElementDisplay(this.optionsEl, 'none');
    this.setElementDisplay(this.notAvailableEl, 'block');
    this.setElementDisplay(this.notApprovedEl, 'none');

    // Clear current artworks and requirements when no artwork is available
    this.currentArtworks = [];
    this.clearArtworkRequirements();
    
    // Dispatch state change to update ATC button
    this.dispatchArtworkStateChange();
  }

  /**
   * Show not approved state
   * @private
   * @param {Object} artwork - Artwork object with details
   */
  showNotApproved(artwork) {
    this.ensureCardElement();

    this.setElementDisplay(this.loadingEl, 'none');
    this.setElementDisplay(this.optionsEl, 'none');
    this.setElementDisplay(this.notAvailableEl, 'none');
    this.setElementDisplay(this.notApprovedEl, 'block');

    this.updateNotApprovedContent(artwork);
    
    // Clear current artworks and requirements when artwork is not approved
    this.currentArtworks = [];
    this.clearArtworkRequirements();
    
    // Dispatch state change to update ATC button
    this.dispatchArtworkStateChange();
  }

  /**
   * Update not approved content with artwork ID
   * @private
   * @param {Object} artwork - Artwork object
   */
  updateNotApprovedContent(artwork) {
    if (artwork?.uuid && this.notApprovedEl) {
      const content = this.notApprovedEl.innerHTML.replace('{{id}}', artwork.uuid);
      this.notApprovedEl.innerHTML = content;
    }
  }

  /**
   * Ensure card element is available
   * @private
   */
  ensureCardElement() {
    if (!this.card) {
      this.card = this.querySelector(ARTWORK_CONFIG.SELECTORS.CARD);
    }
  }

  /**
   * Set element display style safely
   * @private
   * @param {HTMLElement} element - Element to update
   * @param {string} display - Display value
   */
  setElementDisplay(element, display) {
    if (element) {
      element.style.display = display;
    }
  }

  /**
   * Render artwork options in the UI
   * @private
   * @param {Array} artworks - Approved artworks to render
   */
  renderArtworkOptions(artworks) {
    this.ensureCardElement();

    this.hideLoadingAndErrors();
    this.clearArtworkRequirements();
    this.populateArtworkSwatches(artworks);
    
    // Initialize state tracking for all artwork options after rendering
    this.initializeArtworkOptionStates();
    
    this.showOptionsWithTransition();
    this.resetBlankSelectionState();
    this.dispatchArtworkStateChange();
    
    // Save the current input value to localStorage as the last successful search
    if (this.input && this.input.value) {
      localStorage.setItem(ARTWORK_CONFIG.LOCAL_STORAGE.LAST_SEARCH_KEY, this.input.value);
      localStorage.setItem(ARTWORK_CONFIG.LOCAL_STORAGE.ORDER_ENTERED_KEY, 'true');
    }
  }

  /**
   * Initialize state tracking for all artwork options
   * @private
   */
  initializeArtworkOptionStates() {
    if (!this.optionsEl) return;

    const artworkOptionTypes = this.getArtworkOptionTypes();
    
    // Initialize state for each artwork option type
    artworkOptionTypes.forEach(optionType => {
      if (!this.artworkRequirements.has(optionType)) {
        this.artworkRequirements.set(optionType, false);
      }
      
      // Sync with global productFormState
      if (window.productFormState && window.productFormState.requiredSelections) {
        if (!window.productFormState.requiredSelections.artwork) {
          window.productFormState.requiredSelections.artwork = {};
        }
        if (!(optionType in window.productFormState.requiredSelections.artwork)) {
          window.productFormState.requiredSelections.artwork[optionType] = false;
        }
      }
    });
  }

  /**
   * Hide loading and error states
   * @private
   */
  hideLoadingAndErrors() {
    this.setElementDisplay(this.loadingEl, 'none');
    this.setElementDisplay(this.notAvailableEl, 'none');
    this.setElementDisplay(this.notApprovedEl, 'none');
  }

  /**
   * Clear artwork requirements map
   * @private
   */
  clearArtworkRequirements() {
    this.artworkRequirements.clear();
  }

  /**
   * Populate artwork swatches with artwork data
   * @private
   * @param {Array} artworks - Artwork data to populate
   */
  populateArtworkSwatches(artworks) {
    const artworkSwatches = this.optionsEl.querySelectorAll(ARTWORK_CONFIG.SELECTORS.ARTWORK_SWATCHES);

    artworkSwatches.forEach((swatchContainer) => {
      const option = swatchContainer.dataset.option;
      this.populateSwatchContainer(swatchContainer, option, artworks);
    });
  }

  /**
   * Populate individual swatch container
   * @private
   * @param {HTMLElement} swatchContainer - Container element
   * @param {string} option - Option type
   * @param {Array} artworks - Artwork data
   */
  populateSwatchContainer(swatchContainer, option, artworks) {
    swatchContainer.innerHTML = '';
    this.artworkRequirements.set(option, false);
    
    // Initialize in global state
    if (window.productFormState && window.productFormState.requiredSelections) {
      if (!window.productFormState.requiredSelections.artwork) {
        window.productFormState.requiredSelections.artwork = {};
      }
      window.productFormState.requiredSelections.artwork[option] = false;
    }

    const artworkHtml = this.generateArtworkHtml(artworks, option);
    const blankHtml = this.generateBlankOptionHtml(option);

    swatchContainer.innerHTML = artworkHtml + blankHtml;
    
    // Event delegation on optionsEl handles new inputs automatically via bubbling
  }

  /**
   * Generate HTML for artwork options
   * @private
   * @param {Array} artworks - Artwork data
   * @param {string} option - Option type
   * @returns {string} Generated HTML
   */
  generateArtworkHtml(artworks, option) {
    let artworkList = '';

    artworks.forEach((artwork) => {
      if (artwork.png) {
        artwork.png.forEach((png, index) => {
          artworkList += this.generateSingleArtworkHtml(artwork, png, option, index);
        });
      }
    });

    return artworkList;
  }

  /**
   * Generate HTML for single artwork option
   * @private
   * @param {Object} artwork - Artwork object
   * @param {Object} png - PNG data
   * @param {string} option - Option type
   * @param {number} index - PNG index
   * @returns {string} Generated HTML
   */
  generateSingleArtworkHtml(artwork, png, option, index) {
    const inputId = `input-${option}-${artwork.art_id}-${index}`;
    const imageUrl = png.thumbnails?.large?.url || png.url;
    const imageWidth = png.thumbnails?.large?.width || '';
    const imageHeight = png.thumbnails?.large?.height || '';
    const metadataUrl = png.metadata || '';

    return `
      <li class="col p-1">
        <input
          class="artwork-option visually-hidden"
          type="radio"
          name="properties[${option}]"
          id="${inputId}"
          value="${png.url}"
          data-png="${imageUrl}"
          data-artwork-id="${artwork.art_id}"
          data-metadata-url="${metadataUrl}"
          required>
        <label
          for="${inputId}"
          aria-label="${artwork.art_id}-${index}"
          class="text-center d-block"
          style="cursor: pointer">
          <div class="artwork-form__option-image-wrapper">
            <img
              data-aspect-ratio="${imageWidth}/${imageHeight}"
              src="${imageUrl}"
              alt="${artwork.art_id}"
              width="${imageWidth}"
              height="${imageHeight}"
              class="img-thumbnail mb-1"
              loading="lazy">
            </div>
          <small class="text-muted d-block" style="font-size: .875em;">
            ${artwork.art_id}
          </small>
        </label>
      </li>
    `;
  }

  /**
   * Generate HTML for blank option
   * @private
   * @param {string} option - Option type
   * @returns {string} Generated HTML
   */
  generateBlankOptionHtml(option) {
    if (!this.shouldShowBlankOption(option)) {
      return '';
    }

    const svgDataUrl = this.createBlankSvgDataUrl();

    return `
      <li class="col p-1">
        <input
          class="artwork-option visually-hidden"
          type="radio"
          name="properties[${option}]"
          id="input-${option}-blank"
          value="blank"
          data-artwork-id="${ARTWORK_CONFIG.BLANK_ARTWORK_ID}"
          required>
        <label
          for="input-${option}-blank"
          aria-label="Blank"
          class="text-center d-block"
          style="cursor: pointer">

          <div class="artwork-form__option-image-wrapper">
            <img
              src="${svgDataUrl}"
              alt="Blank"
              class="img-thumbnail mb-1"
              loading="lazy">
          </div>

          <small class="text-muted d-block" style="font-size: .875em;">
            Blank
          </small>
        </label>
      </li>
    `;
  }

  /**
   * Check if blank option should be shown for this option type
   * @private
   * @param {string} option - Option type
   * @returns {boolean} Whether to show blank option
   */
  shouldShowBlankOption(option) {
    if (this.disableLeaveBlankInArtwork) return false;
    return option === 'select-front' || option === 'select-back';
  }

  /**
   * Create SVG data URL for blank option
   * @private
   * @returns {string} SVG data URL
   */
  createBlankSvgDataUrl() {
    return "data:image/png;base64,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";
  }

  /**
   * Show options with smooth transition
   * @private
   */
  showOptionsWithTransition() {
    if (!this.optionsEl) return;

    this.optionsEl.style.display = 'block';
    this.optionsEl.style.opacity = '0';
    this.optionsEl.style.height = '0px';
    this.optionsEl.style.overflow = 'hidden';
    this.optionsEl.style.transition = `opacity ${ARTWORK_CONFIG.TRANSITIONS.DURATION}ms ease, height ${ARTWORK_CONFIG.TRANSITIONS.DURATION}ms ease`;

    setTimeout(() => {
      this.optionsEl.style.opacity = '1';
      this.optionsEl.style.height = 'auto';
      this.optionsEl.style.overflow = 'visible';

      // Initialize blank selection state
      this.resetBlankSelectionState();
    }, ARTWORK_CONFIG.TRANSITIONS.DELAY);
  }

  /**
   * Clear artwork display and state
   */
  clearArtwork() {
    this.ensureCardElement();

    this.card.style.display = 'none';
    this.clearArtworkRequirements();
    this.currentArtworks = [];

    this.uncheckArtworkInputs();
    this.resetBlankSelectionState();
    
    this.dispatchArtworkStateChange();
  }

  /**
   * Uncheck all artwork inputs
   * @private
   */
  uncheckArtworkInputs() {
    const artworkInputs = this.querySelectorAll(`${ARTWORK_CONFIG.SELECTORS.ARTWORK_OPTION}:checked`);
    artworkInputs.forEach(input => input.checked = false);
  }

  /**
   * Reset blank selection state to initial state
   * @private
   */
  resetBlankSelectionState() {
    const blankInputs = this.querySelectorAll(`input[data-artwork-id="${ARTWORK_CONFIG.BLANK_ARTWORK_ID}"]`);
    blankInputs.forEach(input => {
      this.setBlankInputState(input, false);
    });
  }

  /**
   * Update product gallery with selected artwork
   * @private
   * @param {HTMLInputElement} selectedInput - Selected artwork input
   */
  updateGalleryWithArtwork(selectedInput) {
    const artworkData = this.extractArtworkData(selectedInput);

    if (artworkData.artworkId == ARTWORK_CONFIG.BLANK_ARTWORK_ID){
      const mediaGallery = document.querySelector('product-media-gallery');
      const targetSlide = this.findTargetSlide(mediaGallery, artworkData.optionType);

      this.updateSlideArtwork(targetSlide, artworkData);
    }

    if (!artworkData.isValid) return;

    const mediaGallery = document.querySelector('product-media-gallery');
    if (!mediaGallery) {
      // Still emit event even if gallery is missing so listeners can react (e.g., price, state)
      this.dispatchArtworkSelectedEvent(artworkData, null);
      return;
    }

    const targetSlide = this.findTargetSlide(mediaGallery, artworkData.optionType);
    if (!targetSlide) {
      // Emit selection event even when no slide exists for this option (e.g., select-logo)
      this.dispatchArtworkSelectedEvent(artworkData, null);
      return;
    }

    this.updateSlideArtwork(targetSlide, artworkData);
    this.navigateToSlide(mediaGallery, targetSlide);
    this.dispatchArtworkSelectedEvent(artworkData, targetSlide);
  }

  /**
   * Extract artwork data from selected input
   * @private
   * @param {HTMLInputElement} selectedInput - Selected input element
   * @returns {Object} Artwork data object
   */
  extractArtworkData(selectedInput) {
    if (!selectedInput) {
      return { imageUrl: null, artworkId: null, optionType: null, artworkAspectRatio: null, metadataUrl: null, isValid: false };
    }

    const imageUrl = selectedInput.dataset?.png;
    const artworkId = selectedInput.dataset?.artworkId;
    const optionType = this.extractArtworkType(selectedInput);
    const metadataUrl = selectedInput.dataset?.metadataUrl;
    // Prefer ratio from adjacent thumbnail img[data-aspect-ratio], else compute from natural size if available
    let artworkAspectRatio = selectedInput.dataset?.aspectRatio;
    if (!artworkAspectRatio) {
      const container = selectedInput.closest('li') || selectedInput.parentElement;
      const thumbImg = container?.querySelector('img[data-aspect-ratio]') || container?.querySelector('img');
      const ratioAttr = thumbImg?.getAttribute?.('data-aspect-ratio');
      if (ratioAttr && ratioAttr.includes('/')) {
        const [wStr, hStr] = ratioAttr.split('/');
        const w = parseFloat(wStr);
        const h = parseFloat(hStr);
        if (w > 0 && h > 0) artworkAspectRatio = (w / h).toString();
      } else if (thumbImg && thumbImg.naturalWidth > 0 && thumbImg.naturalHeight > 0) {
        artworkAspectRatio = (thumbImg.naturalWidth / thumbImg.naturalHeight).toString();
      }
    }
    return {
      imageUrl,
      artworkId,
      optionType,
      artworkAspectRatio,
      metadataUrl,
      isValid: !!(imageUrl && artworkId && optionType)
    };
  }

  /**
   * Find target slide for artwork option type
   * @private
   * @param {HTMLElement} mediaGallery - Media gallery element
   * @param {string} optionType - Option type
   * @returns {HTMLElement|null} Target slide element
   */
  findTargetSlide(mediaGallery, optionType) {
    const mainSlider = mediaGallery.querySelector('.product-media-gallery__main-slider .swiper-wrapper');
    if (!mainSlider) return null;

    return this.findTargetSlideForOption(mainSlider, optionType);
  }

  /**
   * Universal artwork option to mockup slide mapping system
   * Supports any product type with flexible option naming:
   * - Single artwork products: select-artwork → select-front mockup
   * - Multi artwork products: select-front/select-back → respective mockups
   * - Custom products: any option type → matching mockup attribute
   * @private
   * @param {HTMLElement} mainSlider - Main slider element
   * @param {string} optionType - Option type
   * @returns {HTMLElement|null} Target slide element
   */
  findTargetSlideForOption(mainSlider, optionType) {
    const mappingStrategies = [
      // Strategy 1: Direct match - option matches mockup attribute exactly
      () => mainSlider.querySelector(`.swiper-slide[data-mockup="${optionType}"]`),

      // Strategy 2: Custom mockup attributes for front/back
      () => {
        if (optionType === 'select-front' || optionType === 'select-artwork') {
          return mainSlider.querySelector('.swiper-slide[data-mockup="select-front"], .swiper-slide[data-mockup-custom]');
        }
        if (optionType === 'select-back') {
          return mainSlider.querySelector('.swiper-slide[data-mockup="select-back"], .swiper-slide[data-mockup-custom-back]');
        }
        return null;
      },

      // Strategy 3: Fallback to any front mockup for single artwork products
      () => {
        if (optionType === 'select-artwork') {
          return mainSlider.querySelector('.swiper-slide[data-mockup-custom], .swiper-slide[data-mockup="select-front"]');
        }
        return null;
      },

      // Strategy 4: Generic mockup matching by removing select- prefix
      () => {
        const mockupType = optionType.replace('select-', '');
        return mainSlider.querySelector(`.swiper-slide[data-mockup="${mockupType}"]`);
      }
    ];

    // Try each mapping strategy until one succeeds
    for (const strategy of mappingStrategies) {
      const slide = strategy();
      if (slide) {
        return slide;
      }
    }

    console.warn(`No mockup slide found for option type: ${optionType}`);
    return null;
  }

  /**
   * Update slide with artwork or clear it for blank
   * @private
   * @param {HTMLElement} targetSlide - Target slide element
   * @param {Object} artworkData - Artwork data
   */
  updateSlideArtwork(targetSlide, artworkData) {
    let artworkContainer = targetSlide.querySelector('.artwork-img-container');

    if (!artworkContainer) {
      console.warn(`No artwork container found in slide for ${artworkData.optionType}`);
      return;
    }

    if (artworkData.artworkId === ARTWORK_CONFIG.BLANK_ARTWORK_ID) {
      this.clearSlideArtwork(artworkContainer);
    } else {
      this.setSlideArtwork(artworkContainer, artworkData);
    }
  }

  /**
   * Clear artwork from slide (for blank selection)
   * @private
   * @param {HTMLElement} artworkContainer - Artwork container element
   */
  clearSlideArtwork(artworkContainer) {
    artworkContainer.innerHTML = '';
    artworkContainer.style.display = 'none';
  }

  /**
   * Set artwork on slide
   * @private
   * @param {HTMLElement} artworkContainer - Artwork container element
   * @param {Object} artworkData - Artwork data
   */
  setSlideArtwork(artworkContainer, artworkData) {
    artworkContainer.innerHTML = `
      <img
        src="${artworkData.imageUrl}"
        alt="Artwork: ${artworkData.artworkId}"
        class="artwork-overlay-img position-absolute"
        style="top: 0; left: 0; width: 100%; height: 100%; object-fit: contain; pointer-events: none; z-index: 10;"
        loading="lazy">
    `;
    artworkContainer.style.display = 'block';
  }

  /**
   * Navigate gallery to target slide
   * @private
   * @param {HTMLElement} mediaGallery - Media gallery element
   * @param {HTMLElement} targetSlide - Target slide element
   */
  navigateToSlide(mediaGallery, targetSlide) {
    const mainSlider = mediaGallery.querySelector('.product-media-gallery__main-slider .swiper-wrapper');
    const allSlides = mainSlider.querySelectorAll('.swiper-slide');
    const slideIndex = Array.from(allSlides).indexOf(targetSlide);

    if (slideIndex >= 0) {
      const swiperInstance = this.findSwiperInstance(mediaGallery);
      if (swiperInstance) {
        swiperInstance.slideTo(slideIndex);
      } else {
        console.warn('Could not find Swiper instance to navigate to artwork slide');
      }
    }
  }

  /**
   * Find Swiper instance for navigation
   * @private
   * @param {HTMLElement} mediaGallery - Media gallery element
   * @returns {Object|null} Swiper instance or null
   */
  findSwiperInstance(mediaGallery) {
    // Try multiple methods to find Swiper instance
    const swiperContainer = mediaGallery.querySelector('.product-media-gallery__main-slider');
    if (swiperContainer?.swiper) {
      return swiperContainer.swiper;
    }

    if (mediaGallery.activeMainSlider) {
      return mediaGallery.activeMainSlider;
    }

    const productComponent = document.querySelector('product-info');
    const galleryComponent = productComponent?.querySelector('product-media-gallery');
    if (galleryComponent?.activeMainSlider) {
      return galleryComponent.activeMainSlider;
    }

    return null;
  }

  /**
   * Dispatch artwork selected event
   * @private
   * @param {Object} artworkData - Artwork data
   * @param {HTMLElement} targetSlide - Target slide element
   */
  dispatchArtworkSelectedEvent(artworkData, targetSlide) {
    let slideIndex = -1;
    if (targetSlide && targetSlide.parentElement) {
      const allSlides = targetSlide.parentElement.querySelectorAll('.swiper-slide');
      slideIndex = Array.from(allSlides).indexOf(targetSlide);
    }

    const event = new CustomEvent('artwork:selected', {
      detail: {
        imageUrl: artworkData.imageUrl,
        artworkId: artworkData.artworkId,
        optionType: artworkData.optionType,
        targetSlide: targetSlide || null,
        slideIndex: slideIndex,
        artworkAspectRatio: artworkData.artworkAspectRatio || null,
        metadataUrl: artworkData.metadataUrl || null
      },
      bubbles: true
    });

    console.log('artworkAspectRatio', artworkData.artworkAspectRatio);

    this.dispatchEvent(event);
  }

  /**
   * Dispatch artwork state change event
   */
  dispatchArtworkStateChange() {
    const artworkFields = Array.from(this.artworkRequirements.keys());
    const allArtworkSelected = artworkFields.length === 0 ||
        artworkFields.every(field => this.artworkRequirements.get(field));

    const state = {
      isSearching: this.isSearching,
      hasInput: this.input?.value?.trim().length > 0,
      artworkRequirements: Object.fromEntries(this.artworkRequirements),
      allArtworkSelected,
      artworkFound: this.currentArtworks.length > 0,
      artworkCount: this.currentArtworks.length
    };

    const event = new CustomEvent('artwork:statechange', {
      detail: state,
      bubbles: true
    });

    this.dispatchEvent(event);
  }

  /**
   * Get current artwork state
   * @returns {Object} Current artwork state
   */
  getArtworkState() {
    const artworkFields = Array.from(this.artworkRequirements.keys());
    const allArtworkSelected = artworkFields.length === 0 ||
        artworkFields.every(field => this.artworkRequirements.get(field));

    return {
      isSearching: this.isSearching,
      hasInput: this.input?.value?.trim().length > 0,
      inputValue: this.input?.value || '',
      artworkRequirements: Object.fromEntries(this.artworkRequirements),
      allArtworkSelected,
      artworkFound: this.currentArtworks.length > 0,
      artworkCount: this.currentArtworks.length
    };
  }

  /**
   * Clear search input and artwork
   */
  clearSearch() {
    if (this.input) {
      this.input.value = '';
    }
    this.clearArtwork();
  }

  /**
   * Restore artwork state from saved data
   * @param {Object} state - Saved artwork state
   */
  restoreArtworkState(state) {
    if (!state) return;

    if (this.input && state.hasInput) {
      this.input.value = state.inputValue || '';
    }

    this.artworkRequirements = new Map(Object.entries(state.artworkRequirements));

    if (state.artworkFound && this.input?.value) {
      setTimeout(() => this.searchArtwork(this.input.value), 100);
    }

    this.resetBlankSelectionState();

    setTimeout(() => {
      if (this.shouldApplyBlankRestrictions()) {
        const allOptionTypes = this.getArtworkOptionTypes();

        for (const optionType of allOptionTypes) {
          const selectedBlank = this.querySelector(`input[data-artwork-id="${ARTWORK_CONFIG.BLANK_ARTWORK_ID}"][name="properties[${optionType}]"]:checked`);
          if (selectedBlank) {
            this.handleBlankSelection(optionType);
            break;
          }
        }
      }
    }, 150);
  }

  /**
   * Get artwork input value
   * @returns {string} Current input value
   */
  getArtworkInputValue() {
    return this.input?.value || '';
  }

  /**
   * Prefill input from localStorage if available and input is empty
   * @private
   */
  handleLocalStoragePrefill() {
    if (this.input && !this.input.value) {
      const savedValue = localStorage.getItem(ARTWORK_CONFIG.LOCAL_STORAGE.LAST_SEARCH_KEY);
      if (savedValue) {
        this.input.value = savedValue;
        window.dispatchEvent(new CustomEvent('artwork:prefill', { detail: savedValue }));
        setTimeout(() => this.searchArtwork(savedValue), 100);
      }
    }
  }

  /**
   * Set up form submission validation
   * @private
   */
  setupFormValidation() {
    const form = this.findActualFormElement();
    
    if (!form) return;

    // Listen for form submission attempts - use capture phase to run early
    const handleFormSubmit = (e) => {
      // Ensure all artwork properties are in the form BEFORE validation
      this.ensureAllArtworkPropertiesInForm(form);
      
      if (!this.validateArtworkBeforeSubmit(form)) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      
      // Final check - ensure properties are still in form right before submission
      this.ensureAllArtworkPropertiesInForm(form);
      
      // One more check after a tiny delay to catch any app integrations
      setTimeout(() => {
        this.ensureAllArtworkPropertiesInForm(form);
      }, 0);
    };
    
    form.addEventListener('submit', handleFormSubmit, { capture: true });
    
    // Also listen at document level to catch app integrations that might intercept
    document.addEventListener('submit', (e) => {
      if (e.target === form || form.contains(e.target)) {
        handleFormSubmit(e);
      }
    }, { capture: true });

    // Also validate on form submit button click as backup
    const submitButton = form.querySelector('button[type="submit"]') ||
                        form.querySelector('.btn-atc') ||
                        form.querySelector('[type="submit"]');
    
    if (submitButton) {
      submitButton.addEventListener('click', (e) => {
        if (!this.validateArtworkBeforeSubmit(form)) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      }, { capture: true });
    }
  }

  /**
   * Find the actual form element that will be submitted
   * @private
   * @returns {HTMLFormElement|null} The form element
   */
  findActualFormElement() {
    // Try multiple strategies to find the form
    // 1. Check if artwork-form is inside a form
    let form = this.closest('form[action="/cart/add"]') || this.closest('form');
    
    // 2. If not found, search from document (might be a sibling or parent container)
    if (!form) {
      form = document.querySelector('form[action="/cart/add"]');
    }
    
    // 3. Check if inputs are actually inside the form we found
    if (form) {
      const artworkInput = this.querySelector('input[type="radio"][name^="properties[select-"]:checked');
      if (artworkInput && !form.contains(artworkInput)) {
        // Input is not inside this form - might be outside or in a different form context
        // Try to find form that contains the submit button
        const submitButton = document.querySelector('button[type="submit"].btn-atc') ||
                           document.querySelector('button[type="submit"]');
        if (submitButton) {
          form = submitButton.closest('form');
        }
      }
    }
    
    return form;
  }

  /**
   * Verify artwork input is inside the form element
   * @private
   * @param {HTMLInputElement} input - Input element
   * @param {HTMLFormElement} form - Form element
   * @returns {boolean} Whether input is inside form
   */
  isInputInsideForm(input, form) {
    if (!input || !form) return false;
    
    // Check if input is a descendant of the form
    if (form.contains(input)) return true;
    
    // Check if artwork-form is inside the form
    if (form.contains(this)) return true;
    
    return false;
  }

  /**
   * Ensure all artwork properties are included in the form
   * @private
   * @param {HTMLFormElement} form - Form element
   */
  ensureAllArtworkPropertiesInForm(form) {
    const artworkOptionTypes = this.getArtworkOptionTypes();
    
    for (const optionType of artworkOptionTypes) {
      const artworkInput = this.querySelector(`input[name="properties[${optionType}]"]:checked`) ||
                          form.querySelector(`input[name="properties[${optionType}]"]:checked`) ||
                          document.querySelector(`input[name="properties[${optionType}]"]:checked`);
      
      if (artworkInput && artworkInput.checked && artworkInput.value) {
        this.ensureArtworkPropertyInForm(form, optionType, artworkInput);
      }
    }
  }

  /**
   * Ensure artwork property is included in form data
   * @private
   * @param {HTMLFormElement} form - Form element
   * @param {string} optionType - Artwork option type
   * @param {HTMLInputElement} artworkInput - Selected artwork input
   * @returns {boolean} Whether property was ensured
   */
  ensureArtworkPropertyInForm(form, optionType, artworkInput) {
    if (!form || !artworkInput || !artworkInput.checked || !artworkInput.value) {
      return false;
    }

    // Always ensure a hidden input exists directly in the form (not nested in artwork-form)
    // This is critical for app integrations that may collect form data differently
    const propertyName = `properties[${optionType}]`;
    let hiddenInput = form.querySelector(`input[type="hidden"][name="${propertyName}"]`);
    
    // Remove any hidden inputs that might be nested (we want it directly in form)
    if (hiddenInput && hiddenInput.closest('artwork-form')) {
      hiddenInput.remove();
      hiddenInput = null;
    }
    
    if (!hiddenInput) {
      // Create a hidden input directly in the form to ensure the property is submitted
      hiddenInput = document.createElement('input');
      hiddenInput.type = 'hidden';
      hiddenInput.name = propertyName;
      hiddenInput.setAttribute('data-artwork-form-hidden', 'true'); // Mark as ours
      hiddenInput.setAttribute('data-artwork-option-type', optionType); // For debugging
      
      // Insert at the beginning of the form to ensure it's included
      form.insertBefore(hiddenInput, form.firstChild);
      console.log(`Created hidden input for ${optionType} directly in form to ensure property is submitted`);
    }
    
    // Always update the value to match the selected artwork
    const artworkValue = artworkInput.value.trim();
    if (hiddenInput.value !== artworkValue) {
      hiddenInput.value = artworkValue;
      console.log(`Updated hidden input for ${optionType} with value: ${artworkValue.substring(0, 50)}...`);
    }
    
    return true;
  }

  /**
   * Validate artwork is selected before form submission
   * @private
   * @param {HTMLFormElement} form - Form element
   * @returns {boolean} Whether validation passed
   */
  validateArtworkBeforeSubmit(form) {
    // Find the actual form element that will be submitted
    const actualForm = this.findActualFormElement() || form;
    
    // Get all artwork option types that should be required
    const artworkOptionTypes = this.getArtworkOptionTypes();
    
    if (artworkOptionTypes.length === 0) {
      // No artwork options required for this product
      return true;
    }

    // Check if artwork input has value
    if (!this.input || !this.input.value || this.input.value.trim() === '') {
      console.warn('Artwork form: Order number/email input is empty');
      return false;
    }

    // Check if artworks were found and loaded
    if (this.currentArtworks.length === 0) {
      console.warn('Artwork form: No artworks loaded');
      return false;
    }

    // Validate each required artwork option is selected
    let allValid = true;
    const missingOptions = [];

    for (const optionType of artworkOptionTypes) {
      // Search for the input - try multiple strategies to find it
      let artworkInput = this.querySelector(`input[name="properties[${optionType}]"]:checked`);
      
      // If not found in artwork-form, try searching more broadly
      if (!artworkInput) {
        // Try in the actual form
        artworkInput = actualForm?.querySelector(`input[name="properties[${optionType}]"]:checked`);
      }
      
      // If still not found, search from document root (might be outside form)
      if (!artworkInput) {
        artworkInput = document.querySelector(`input[name="properties[${optionType}]"]:checked`);
      }
      
      if (!artworkInput || !artworkInput.checked) {
        allValid = false;
        missingOptions.push(optionType);
        console.warn(`Artwork form: Option ${optionType} is not selected`);
        continue;
      }

      // Verify the input has a value
      if (!artworkInput.value || artworkInput.value.trim() === '') {
        allValid = false;
        missingOptions.push(optionType);
        console.warn(`Artwork form: Option ${optionType} is selected but has no value`);
        continue;
      }

      // Ensure the property will be in form data
      // First check if input is inside the form
      if (!this.isInputInsideForm(artworkInput, actualForm)) {
        // Input is outside the form - ensure it's included
        if (!this.ensureArtworkPropertyInForm(actualForm, optionType, artworkInput)) {
          console.warn(`Artwork form: Could not ensure property ${optionType} is in form`);
          // Continue validation anyway - the input is checked and has a value
        }
      }

      // Input is checked and has a value - validation PASSES for this option
      // Ensure the property is in the form for submission
      this.ensureArtworkPropertyInForm(actualForm, optionType, artworkInput);
      
      // Log success - the input being checked with a value is sufficient validation
      // FormData check is informational only, not a blocker
      const formData = new FormData(actualForm);
      const propertyValue = formData.get(`properties[${optionType}]`);
      
      if (propertyValue && propertyValue.trim() !== '') {
        console.log(`✓ Artwork property ${optionType} validated and in FormData`);
      } else {
        // Not in FormData, but input is valid - this is OK
        // App integrations may handle form submission differently
        console.log(`✓ Artwork property ${optionType} validated (input checked with value, will be ensured in form)`);
      }
    }

    // Final check: if inputs are checked and valid but not in FormData,
    // ensure they're added to the form before submission
    for (const optionType of artworkOptionTypes) {
      const artworkInput = this.querySelector(`input[name="properties[${optionType}]"]:checked`) ||
                          actualForm.querySelector(`input[name="properties[${optionType}]"]:checked`);
      
      if (artworkInput && artworkInput.checked && artworkInput.value) {
        // Ensure it's in the form one more time before allowing submission
        this.ensureArtworkPropertyInForm(actualForm, optionType, artworkInput);
      }
    }

    if (!allValid) {
      console.error(`Artwork form validation failed. Missing: ${missingOptions.join(', ')}`);
      console.error('Artwork selection status:', this.getArtworkSelectionStatus());
      
      // Try to show user feedback
      const submitButton = actualForm.querySelector('button[type="submit"]') ||
                          actualForm.querySelector('.btn-atc') ||
                          document.querySelector('button[type="submit"].btn-atc');
      
      if (submitButton && missingOptions.length > 0) {
        const optionName = missingOptions[0].replace('select-', '').replace(/-/g, ' ');
        const originalText = submitButton.textContent || submitButton.dataset.textAddToCart || 'Add to Cart';
        
        submitButton.disabled = true;
        submitButton.textContent = `Please select ${optionName}`;
        
        // Restore button after a delay
        setTimeout(() => {
          submitButton.disabled = false;
          submitButton.textContent = originalText;
        }, 3000);
      }
      
      // Emit a validation failed event for debugging
      this.dispatchEvent(new CustomEvent('artwork:validationfailed', {
        detail: {
          missingOptions,
          status: this.getArtworkSelectionStatus()
        },
        bubbles: true
      }));
    } else {
      // All valid - log success for debugging
      console.log('✓ Artwork form validation passed for all options');
      
      // Ensure all properties are in the form one final time
      for (const optionType of artworkOptionTypes) {
        const artworkInput = this.querySelector(`input[name="properties[${optionType}]"]:checked`);
        if (artworkInput) {
          this.ensureArtworkPropertyInForm(actualForm, optionType, artworkInput);
        }
      }
    }

    return allValid;
  }

  /**
   * Initialize existing artwork selections if form is loaded with pre-selected values
   * @private
   */
  initializeExistingSelections() {
    if (!this.optionsEl) return;

    // Find all checked artwork inputs
    const checkedInputs = this.optionsEl.querySelectorAll('input[type="radio"]:checked');
    
    checkedInputs.forEach(input => {
      const artworkType = this.extractArtworkType(input);
      if (artworkType && input.checked) {
        // Validate the input has a value
        if (input.value && input.value.trim() !== '') {
          this.updateArtworkRequirement(artworkType, true);
          this.syncWithProductFormState(artworkType, true);
        } else {
          // If checked but no value, uncheck it
          console.warn(`Artwork input for ${artworkType} is checked but has no value, unchecking`);
          input.checked = false;
          this.updateArtworkRequirement(artworkType, false);
          this.syncWithProductFormState(artworkType, false);
        }
      }
    });

    // Dispatch state change to notify listeners
    if (checkedInputs.length > 0) {
      this.dispatchArtworkStateChange();
    }
  }

  /**
   * Get current artwork selection status for debugging
   * @returns {Object} Current artwork selection status
   */
  getArtworkSelectionStatus() {
    const status = {
      hasInput: !!this.input && !!this.input.value,
      inputValue: this.input?.value || '',
      artworksFound: this.currentArtworks.length,
      optionTypes: this.getArtworkOptionTypes(),
      selections: {},
      formData: {}
    };

    const form = this.closest('form[action="/cart/add"]') || 
                 this.closest('form') ||
                 document.querySelector('form[action="/cart/add"]');

    if (form) {
      const formData = new FormData(form);
      status.optionTypes.forEach(optionType => {
        const input = form.querySelector(`input[name="properties[${optionType}]"]:checked`);
        status.selections[optionType] = {
          checked: !!input && input.checked,
          hasValue: !!input && !!input.value,
          value: input?.value || null,
          inFormData: formData.has(`properties[${optionType}]`),
          formDataValue: formData.get(`properties[${optionType}]`) || null
        };
      });
    }

    return status;
  }
}

// Register the custom element
customElements.define('artwork-form', ArtworkForm);